import { Dispatch, MutableRefObject, SetStateAction } from 'react'
import { Editor } from '@tiptap/react'
import { Extension, Node } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import { Color } from '@tiptap/extension-color'
import ListItem from '@tiptap/extension-list-item'
import BulletList from '@tiptap/extension-bullet-list'
import TextStyle from '@tiptap/extension-text-style'
import Table from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import DfNode from '../extensions/DfNode'
import StNode from '../extensions/StNode'
import { customKeymap } from '../extensions/customKeymap'
import { EditableInputRule } from '../extensions/inputRule'
import { preprocessContentForTiptap } from './processContent'
import { EditorState } from '../../../context/template-context'
import { TemplateExtension } from '../extensions/template-extension'

export const getEditorConfig = (
  initialContent: string,
  readonly: boolean,
  cleanDocRef: MutableRefObject<Node | null>,
  setUpdatedTemplateContent: Dispatch<SetStateAction<EditorState>>
) => {
  const processedInitialContent = preprocessContentForTiptap(initialContent)
  const KeymapExtension = Extension.create({
    name: 'customKeymapExtension',
    addKeyboardShortcuts() {
      return customKeymap
    }
  })

  let onCreate
  let onUpdate

  if (!readonly) {
    onCreate = ({ editor }) => {
      cleanDocRef.current = editor.state.doc
      setUpdatedTemplateContent(prevValue => ({
        ...prevValue,
        editor,
        isDirty: false
      }))
    }

    onUpdate = ({ editor }) => {
      const currentDoc = editor.state.doc
      const isContentEqual = cleanDocRef.current?.eq(currentDoc) // Use ProseMirror's efficient .eq() method

      if (!isContentEqual) {
        setUpdatedTemplateContent(prevValue => ({
          ...prevValue,
          editor,
          isDirty: true
        }))
      } else {
        setUpdatedTemplateContent(prevValue => ({
          ...prevValue,
          editor,
          isDirty: false
        }))
      }
    }
  }

  return {
    extensions: [
      Color.configure({ types: [TextStyle.name, ListItem.name] }),
      TextStyle.configure({
        types: [ListItem.name]
      }),
      Underline,
      StarterKit.configure(),
      DfNode,
      StNode,
      Extension.create(EditableInputRule),
      KeymapExtension,
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'template-tooling-table'
        }
      }),
      TableRow,
      TableHeader,
      TableCell,
      BulletList.configure({
        HTMLAttributes: {
          class: 'template-tooling-list' // Add your class here
        }
      }),
      TemplateExtension.configure() // `configure()` creates separate instance for each editor
    ],
    content: processedInitialContent,
    editable: !readonly,
    onCreate,
    onUpdate
  }
}

export const insertDfNode = (editor: Editor, name: string) => {
  if (!editor || !name) {
    return
  }

  editor
    .chain()
    .focus()
    .insertContent({
      type: 'dfNode',
      attrs: { dfName: name }
    })
    .run()
}

export const insertStNode = (editor: Editor, name: string) => {
  if (!editor || !name) {
    return
  }

  editor
    .chain()
    .focus()
    .insertContent({
      type: 'stNode',
      attrs: { stName: name }
    })
    .run()
}
